import { useState, useEffect } from "react";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Edit2,
  Trash2,
  ChevronUp,
  ChevronDown,
  Loader2,
  Save,
  RotateCcw,
  Settings,
  CreditCard,
} from "lucide-react";
import {
  registrationApi,
  type RegistrationConfig,
  type FormFieldConfig,
  type PatronTypeRegistrationConfig,
} from "@/lib/api/registration";
import { type PatronType } from "@/lib/api/patron-types";
import { usePatronTypes } from "@/hooks/usePatronTypes";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
  { value: "radio", label: "Radio" },
  { value: "textarea", label: "Textarea" },
  { value: "date", label: "Date" },
  { value: "file", label: "File Upload" },
] as const;

const RegistrationSetupPage = () => {
  const { isTenantAdmin } = useAccessControl();
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const saving = pendingKey !== null;
  const [activeTab, setActiveTab] = useState<string>("general");
  const [searchQuery, setSearchQuery] = useState("");

  // General (tenant-level) configuration
  const [generalConfig, setGeneralConfig] = useState<RegistrationConfig>({
    allowSelfRegistration: true,
    verificationMode: "auto",
    requirePayment: false,
    defaultFeeAmount: 0,
    requireProfilePicture: false,
    showBranchPreference: false,
    requireDocuments: [],
    formFields: [],
  });

  // Patron-type-specific configurations
  const [patronTypeConfigs, setPatronTypeConfigs] = useState<
    Record<string, PatronTypeRegistrationConfig>
  >({});

  const { data: allPatronTypes = [], isLoading: patronTypesLoading } = usePatronTypes(true, {
    enabled: isTenantAdmin,
  });
  const patronTypes = allPatronTypes.filter((pt) => pt.isActive);

  // UI state
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<FormFieldConfig | null>(null);
  const [editingFieldType, setEditingFieldType] = useState<"general" | "custom">("general");
  const [newDocumentType, setNewDocumentType] = useState("");

  useEffect(() => {
    if (isTenantAdmin && allPatronTypes.length > 0) {
      loadData();
    }
  }, [isTenantAdmin, allPatronTypes]);

  const loadData = async () => {
    try {
      setLoading(true);
      const configData = await registrationApi.getAdminRegistrationConfig();
      setGeneralConfig(configData);
      const activePatronTypes = patronTypes;

      // Load patron-type-specific configs
      // We'll extract differences by comparing merged configs with general config
      const configs: Record<string, PatronTypeRegistrationConfig> = {};
      const generalFieldIds = new Set(configData.formFields.map((f) => f.id));

      for (const pt of activePatronTypes) {
        try {
          const ptConfig = await registrationApi.getAdminRegistrationConfig(pt.id);

          // Extract custom fields (fields not in general config)
          const customFields = ptConfig.formFields.filter((f) => !generalFieldIds.has(f.id));

          // Extract differences
          configs[pt.id] = {
            customFields: customFields.length > 0 ? customFields : undefined,
            registrationFee:
              ptConfig.defaultFeeAmount !== configData.defaultFeeAmount
                ? ptConfig.defaultFeeAmount
                : undefined,
            autoVerification:
              ptConfig.verificationMode !== configData.verificationMode
                ? ptConfig.verificationMode === "auto"
                : undefined,
            requireDocuments:
              ptConfig.requireDocuments.length > 0 &&
              ptConfig.requireDocuments.some((doc) => !configData.requireDocuments.includes(doc))
                ? ptConfig.requireDocuments.filter(
                    (doc) => !configData.requireDocuments.includes(doc)
                  )
                : undefined,
          };
        } catch (error) {
          console.error(`Failed to load config for patron type ${pt.id}:`, error);
          configs[pt.id] = {};
        }
      }
      setPatronTypeConfigs(configs);
    } catch (error) {
      console.error("Failed to load data:", error);
      alert("Failed to load registration configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    try {
      setPendingKey("general-save");
      await registrationApi.updateRegistrationConfig(generalConfig);
      alert("General registration configuration saved successfully!");
      await loadData();
    } catch (error) {
      console.error("Failed to save:", error);
      alert("Failed to save configuration");
    } finally {
      setPendingKey(null);
    }
  };

  const handleSavePatronType = async (patronTypeId: string) => {
    try {
      setPendingKey(`save:${patronTypeId}`);
      const config = patronTypeConfigs[patronTypeId] || {};
      await registrationApi.updatePatronTypeRegistrationConfig(patronTypeId, config);
      alert("Patron type configuration saved successfully!");
      await loadData();
    } catch (error) {
      console.error("Failed to save:", error);
      alert("Failed to save patron type configuration");
    } finally {
      setPendingKey(null);
    }
  };

  const handleResetGeneral = async () => {
    if (
      !confirm(
        "Are you sure you want to reset general registration configuration to defaults? This will remove all general custom fields and settings."
      )
    ) {
      return;
    }

    try {
      setPendingKey("general-reset");
      const defaultConfig: RegistrationConfig = {
        allowSelfRegistration: true,
        verificationMode: "auto",
        requirePayment: false,
        defaultFeeAmount: 0,
        requireProfilePicture: false,
        showBranchPreference: false,
        requireDocuments: [],
        formFields: [],
      };
      await registrationApi.updateRegistrationConfig(defaultConfig);
      setGeneralConfig(defaultConfig);
      alert("General configuration reset to defaults successfully!");
      await loadData();
    } catch (error) {
      console.error("Failed to reset:", error);
      alert("Failed to reset configuration");
    } finally {
      setPendingKey(null);
    }
  };

  const handleResetPatronType = async (patronTypeId: string) => {
    const patronType = patronTypes.find((pt) => pt.id === patronTypeId);
    if (
      !confirm(
        `Are you sure you want to reset ${patronType?.name || "this patron type"} configuration to defaults?`
      )
    ) {
      return;
    }

    try {
      setPendingKey(`reset:${patronTypeId}`);
      await registrationApi.updatePatronTypeRegistrationConfig(patronTypeId, {});
      setPatronTypeConfigs((prev) => ({ ...prev, [patronTypeId]: {} }));
      alert("Patron type configuration reset successfully!");
      await loadData();
    } catch (error) {
      console.error("Failed to reset:", error);
      alert("Failed to reset patron type configuration");
    } finally {
      setPendingKey(null);
    }
  };

  // Field management for general config
  const handleAddGeneralField = () => {
    const newField: FormFieldConfig = {
      id: `field-${Date.now()}`,
      name: "",
      label: "",
      type: "text",
      required: false,
      order: generalConfig.formFields.length,
    };
    setEditingField(newField);
    setEditingFieldType("general");
    setShowFieldModal(true);
  };

  const handleEditGeneralField = (field: FormFieldConfig) => {
    setEditingField({ ...field });
    setEditingFieldType("general");
    setShowFieldModal(true);
  };

  const handleSaveGeneralField = () => {
    if (!editingField || !editingField.label || !editingField.name) {
      alert("Please fill in label and field name");
      return;
    }

    const updatedFields = [...generalConfig.formFields];
    const existingIndex = updatedFields.findIndex((f) => f.id === editingField.id);

    if (existingIndex >= 0) {
      updatedFields[existingIndex] = editingField;
    } else {
      updatedFields.push(editingField);
    }

    updatedFields.sort((a, b) => a.order - b.order);
    updatedFields.forEach((f, i) => {
      f.order = i;
    });

    setGeneralConfig({ ...generalConfig, formFields: updatedFields });
    setShowFieldModal(false);
    setEditingField(null);
  };

  const handleDeleteGeneralField = (fieldId: string) => {
    if (!confirm("Are you sure you want to delete this field?")) return;
    setGeneralConfig({
      ...generalConfig,
      formFields: generalConfig.formFields.filter((f) => f.id !== fieldId),
    });
  };

  const handleMoveGeneralField = (fieldId: string, direction: "up" | "down") => {
    const fields = [...generalConfig.formFields];
    const index = fields.findIndex((f) => f.id === fieldId);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
    fields.forEach((f, i) => {
      f.order = i;
    });

    setGeneralConfig({ ...generalConfig, formFields: fields });
  };

  // Field management for patron-type-specific custom fields
  const handleAddCustomField = (patronTypeId: string) => {
    const currentConfig = patronTypeConfigs[patronTypeId] || {};
    const customFields = currentConfig.customFields || [];
    const newField: FormFieldConfig = {
      id: `field-${Date.now()}`,
      name: "",
      label: "",
      type: "text",
      required: false,
      order: customFields.length,
    };
    setEditingField(newField);
    setEditingFieldType("custom");
    setShowFieldModal(true);
  };

  const handleEditCustomField = (patronTypeId: string, field: FormFieldConfig) => {
    setEditingField({ ...field });
    setEditingFieldType("custom");
    setShowFieldModal(true);
  };

  const handleSaveCustomField = (patronTypeId: string) => {
    if (!editingField || !editingField.label || !editingField.name) {
      alert("Please fill in label and field name");
      return;
    }

    const currentConfig = patronTypeConfigs[patronTypeId] || {};
    const customFields = [...(currentConfig.customFields || [])];
    const existingIndex = customFields.findIndex((f) => f.id === editingField.id);

    if (existingIndex >= 0) {
      customFields[existingIndex] = editingField;
    } else {
      customFields.push(editingField);
    }

    customFields.sort((a, b) => a.order - b.order);
    customFields.forEach((f, i) => {
      f.order = i;
    });

    setPatronTypeConfigs({
      ...patronTypeConfigs,
      [patronTypeId]: {
        ...currentConfig,
        customFields,
      },
    });
    setShowFieldModal(false);
    setEditingField(null);
  };

  const handleDeleteCustomField = (patronTypeId: string, fieldId: string) => {
    if (!confirm("Are you sure you want to delete this field?")) return;
    const currentConfig = patronTypeConfigs[patronTypeId] || {};
    setPatronTypeConfigs({
      ...patronTypeConfigs,
      [patronTypeId]: {
        ...currentConfig,
        customFields: (currentConfig.customFields || []).filter((f) => f.id !== fieldId),
      },
    });
  };

  const handleMoveCustomField = (
    patronTypeId: string,
    fieldId: string,
    direction: "up" | "down"
  ) => {
    const currentConfig = patronTypeConfigs[patronTypeId] || {};
    const fields = [...(currentConfig.customFields || [])];
    const index = fields.findIndex((f) => f.id === fieldId);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
    fields.forEach((f, i) => {
      f.order = i;
    });

    setPatronTypeConfigs({
      ...patronTypeConfigs,
      [patronTypeId]: {
        ...currentConfig,
        customFields: fields,
      },
    });
  };

  const handleAddGeneralDocument = () => {
    if (!newDocumentType.trim()) return;
    setGeneralConfig({
      ...generalConfig,
      requireDocuments: [...generalConfig.requireDocuments, newDocumentType.trim()],
    });
    setNewDocumentType("");
  };

  const handleRemoveGeneralDocument = (index: number) => {
    setGeneralConfig({
      ...generalConfig,
      requireDocuments: generalConfig.requireDocuments.filter((_, i) => i !== index),
    });
  };

  const handleAddPatronTypeDocument = (patronTypeId: string) => {
    if (!newDocumentType.trim()) return;
    const currentConfig = patronTypeConfigs[patronTypeId] || {};
    setPatronTypeConfigs({
      ...patronTypeConfigs,
      [patronTypeId]: {
        ...currentConfig,
        requireDocuments: [...(currentConfig.requireDocuments || []), newDocumentType.trim()],
      },
    });
    setNewDocumentType("");
  };

  const handleRemovePatronTypeDocument = (patronTypeId: string, index: number) => {
    const currentConfig = patronTypeConfigs[patronTypeId] || {};
    setPatronTypeConfigs({
      ...patronTypeConfigs,
      [patronTypeId]: {
        ...currentConfig,
        requireDocuments: (currentConfig.requireDocuments || []).filter((_, i) => i !== index),
      },
    });
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <h2 className="mb-4 text-2xl font-bold">Access Denied</h2>
            <p className="text-gray-600">You need Tenant Admin permissions to access this page.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loading || (isTenantAdmin && patronTypesLoading)) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <LoadingOverlay size="lg" label="Loading registration settings..." />
        </div>
      </AppLayout>
    );
  }

  const currentPatronType =
    activeTab !== "general" ? patronTypes.find((pt) => pt.id === activeTab) : null;
  const currentPatronTypeConfig = currentPatronType
    ? patronTypeConfigs[currentPatronType.id] || {}
    : null;
  const _allFieldsForPatronType = currentPatronType
    ? [...generalConfig.formFields, ...(currentPatronTypeConfig?.customFields || [])]
    : [];

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Registration Setup"
          description="Configure registration forms for each patron type"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Registration" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search settings..."
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex flex-wrap gap-1 mb-6">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              General
            </TabsTrigger>
            {patronTypes.map((pt) => (
              <TabsTrigger key={pt.id} value={pt.id}>
                {pt.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                {/* General Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle>General Settings</CardTitle>
                    <CardDescription>
                      These settings apply to all patron types unless overridden
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="allowSelfRegistration"
                        checked={generalConfig.allowSelfRegistration}
                        onCheckedChange={(checked) =>
                          setGeneralConfig({
                            ...generalConfig,
                            allowSelfRegistration: checked === true,
                          })
                        }
                      />
                      <Label htmlFor="allowSelfRegistration">Allow Self Registration</Label>
                    </div>

                    <div>
                      <Label htmlFor="verificationMode">Default Verification Mode</Label>
                      <Select
                        value={generalConfig.verificationMode}
                        onValueChange={(value: "auto" | "manual") =>
                          setGeneralConfig({ ...generalConfig, verificationMode: value })
                        }
                      >
                        <SelectTrigger id="verificationMode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (Auto-approve)</SelectItem>
                          <SelectItem value="manual">Manual (Admin approval required)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="requireProfilePicture"
                        checked={generalConfig.requireProfilePicture}
                        onCheckedChange={(checked) =>
                          setGeneralConfig({
                            ...generalConfig,
                            requireProfilePicture: checked === true,
                          })
                        }
                      />
                      <Label htmlFor="requireProfilePicture">Require Profile Picture</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="showBranchPreference"
                        checked={generalConfig.showBranchPreference ?? false}
                        onCheckedChange={(checked) =>
                          setGeneralConfig({
                            ...generalConfig,
                            showBranchPreference: checked === true,
                          })
                        }
                      />
                      <div>
                        <Label htmlFor="showBranchPreference">Show Branch Preference</Label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Patrons can select a preferred home branch during self-registration
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label>Default Required Documents</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={newDocumentType}
                          onChange={(e) => setNewDocumentType(e.target.value)}
                          placeholder="e.g., School Fees Receipt"
                          onKeyPress={(e) => e.key === "Enter" && handleAddGeneralDocument()}
                        />
                        <Button onClick={handleAddGeneralDocument}>Add</Button>
                      </div>
                      <div className="mt-2 space-y-1">
                        {generalConfig.requireDocuments.map((doc, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-100 rounded"
                          >
                            <span className="text-sm">{doc}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveGeneralDocument(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="overflow-hidden border-slate-200/80 bg-gradient-to-br from-slate-50/90 to-white shadow-sm">
                  <CardHeader className="pb-4 space-y-1 border-b border-slate-100 bg-white/60">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 shrink-0">
                        <CreditCard className="w-5 h-5 text-primary" aria-hidden />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <CardTitle className="text-base">Registration fee (default)</CardTitle>
                        <CardDescription>
                          When enabled, new self-registrations must pay this amount before services
                          unlock (unless a patron type sets its own fee).
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-5 space-y-4">
                    <div className="flex flex-wrap items-start gap-3 sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="requirePayment"
                          checked={generalConfig.requirePayment}
                          onCheckedChange={(checked) =>
                            setGeneralConfig({
                              ...generalConfig,
                              requirePayment: checked === true,
                            })
                          }
                          className="mt-0.5"
                        />
                        <div>
                          <Label htmlFor="requirePayment" className="text-sm font-medium">
                            Require payment during registration
                          </Label>
                          <p className="mt-0.5 text-xs text-slate-500">
                            Patrons can still create an account; payment may be completed after
                            email verification when your workflow allows it.
                          </p>
                        </div>
                      </div>
                    </div>
                    {generalConfig.requirePayment && (
                      <div className="p-4 space-y-2 rounded-lg border bg-white border-slate-200/90">
                        <Label htmlFor="defaultFeeAmount" className="text-slate-700">
                          Default fee amount
                        </Label>
                        <Input
                          id="defaultFeeAmount"
                          type="number"
                          min={0}
                          step="0.01"
                          className="max-w-xs font-mono tabular-nums"
                          value={generalConfig.defaultFeeAmount || 0}
                          onChange={(e) =>
                            setGeneralConfig({
                              ...generalConfig,
                              defaultFeeAmount: parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                        <p className="text-xs text-slate-500">
                          Patron types can override this under their own tab.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* General Form Fields */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>General Form Fields</CardTitle>
                        <CardDescription>
                          Fields that appear for all patron types ({generalConfig.formFields.length}{" "}
                          field
                          {generalConfig.formFields.length !== 1 ? "s" : ""})
                        </CardDescription>
                      </div>
                      <Button onClick={handleAddGeneralField}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Field
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {generalConfig.formFields.length === 0 ? (
                        <p className="py-8 text-sm text-center text-gray-500">
                          No general fields configured. Click "Add Field" to get started.
                        </p>
                      ) : (
                        generalConfig.formFields.map((field, index) => (
                          <div
                            key={field.id}
                            className="flex items-center justify-between p-3 transition-colors border rounded-lg hover:bg-gray-50"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-gray-400">
                                  #{index + 1}
                                </span>
                                <span className="font-medium">
                                  {field.label || "Unnamed Field"}
                                </span>
                                <span className="text-xs text-gray-500">({field.type})</span>
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                  General
                                </span>
                                {field.required && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                    Required
                                  </span>
                                )}
                              </div>
                              {field.placeholder && (
                                <p className="mt-1 ml-6 text-xs text-gray-500">
                                  Placeholder: {field.placeholder}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveGeneralField(field.id, "up")}
                                disabled={field.order === 0}
                              >
                                <ChevronUp className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveGeneralField(field.id, "down")}
                                disabled={field.order === generalConfig.formFields.length - 1}
                              >
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditGeneralField(field)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteGeneralField(field.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Preview Panel */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle>Live Preview</CardTitle>
                    <CardDescription>How your general form will look</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {generalConfig.formFields.length === 0 ? (
                        <p className="py-8 text-sm text-center text-gray-500">
                          No fields to preview
                        </p>
                      ) : (
                        generalConfig.formFields.map((field) => (
                          <div key={field.id}>
                            <Label>
                              {field.label}
                              {field.required && <span className="text-red-500">*</span>}
                            </Label>
                            {field.type === "text" && (
                              <Input placeholder={field.placeholder} disabled />
                            )}
                            {field.type === "email" && (
                              <Input type="email" placeholder={field.placeholder} disabled />
                            )}
                            {field.type === "number" && (
                              <Input type="number" placeholder={field.placeholder} disabled />
                            )}
                            {field.type === "textarea" && (
                              <textarea
                                className="w-full min-h-[80px] px-3 py-2 border rounded-md"
                                placeholder={field.placeholder}
                                disabled
                              />
                            )}
                            {field.type === "select" && field.options && (
                              <Select disabled>
                                <SelectTrigger>
                                  <SelectValue placeholder={field.placeholder || "Select..."} />
                                </SelectTrigger>
                                <SelectContent>
                                  {field.options.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <Button
                onClick={handleResetGeneral}
                disabled={saving}
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {pendingKey === "general-reset" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="mr-2 h-4 w-4" />
                )}
                Reset to Defaults
              </Button>
              <Button onClick={handleSaveGeneral} disabled={saving} size="lg">
                {pendingKey === "general-save" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save General Configuration
              </Button>
            </div>
          </TabsContent>

          {/* Patron Type Tabs */}
          {patronTypes.map((patronType) => {
            const ptConfig = patronTypeConfigs[patronType.id] || {};
            const customFields = ptConfig.customFields || [];
            const allFields = [...generalConfig.formFields, ...customFields];

            return (
              <TabsContent key={patronType.id} value={patronType.id} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="space-y-6 lg:col-span-2">
                    {/* Patron Type Overrides */}
                    <Card>
                      <CardHeader>
                        <CardTitle>{patronType.name} - Specific Settings</CardTitle>
                        <CardDescription>
                          Override general settings for this patron type
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label htmlFor={`verification-${patronType.id}`}>Verification Mode</Label>
                          <Select
                            value={
                              ptConfig.autoVerification === true
                                ? "auto"
                                : ptConfig.autoVerification === false
                                  ? "manual"
                                  : "inherit"
                            }
                            onValueChange={(value) => {
                              setPatronTypeConfigs({
                                ...patronTypeConfigs,
                                [patronType.id]: {
                                  ...ptConfig,
                                  autoVerification:
                                    value === "inherit" ? undefined : value === "auto",
                                },
                              });
                            }}
                          >
                            <SelectTrigger id={`verification-${patronType.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inherit">
                                Inherit from General ({generalConfig.verificationMode})
                              </SelectItem>
                              <SelectItem value="auto">Auto (Auto-approve)</SelectItem>
                              <SelectItem value="manual">
                                Manual (Admin approval required)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor={`fee-${patronType.id}`}>Registration Fee</Label>
                          <Input
                            id={`fee-${patronType.id}`}
                            type="number"
                            value={ptConfig.registrationFee ?? generalConfig.defaultFeeAmount ?? 0}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              setPatronTypeConfigs({
                                ...patronTypeConfigs,
                                [patronType.id]: {
                                  ...ptConfig,
                                  registrationFee:
                                    value === generalConfig.defaultFeeAmount ? undefined : value,
                                },
                              });
                            }}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Leave empty or same as general to inherit (
                            {generalConfig.defaultFeeAmount || 0})
                          </p>
                        </div>

                        <div>
                          <Label>Required Documents for {patronType.name}</Label>
                          <p className="mb-2 text-xs text-gray-500">
                            {ptConfig.requireDocuments && ptConfig.requireDocuments.length > 0
                              ? "These documents replace the general requirements for this patron type."
                              : "If left empty, will inherit from general requirements."}
                          </p>
                          <div className="flex gap-2 mt-2">
                            <Input
                              value={newDocumentType}
                              onChange={(e) => setNewDocumentType(e.target.value)}
                              placeholder="e.g., Student ID Card"
                              onKeyPress={(e) =>
                                e.key === "Enter" && handleAddPatronTypeDocument(patronType.id)
                              }
                            />
                            <Button onClick={() => handleAddPatronTypeDocument(patronType.id)}>
                              Add
                            </Button>
                          </div>
                          <div className="mt-2 space-y-1">
                            {(ptConfig.requireDocuments || []).map((doc, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-2 bg-gray-100 rounded"
                              >
                                <span className="text-sm">{doc}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleRemovePatronTypeDocument(patronType.id, index)
                                  }
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                            {(!ptConfig.requireDocuments ||
                              ptConfig.requireDocuments.length === 0) && (
                              <p className="py-2 text-xs italic text-gray-500">
                                No patron-type-specific documents. Will use general requirements.
                              </p>
                            )}
                          </div>
                          {generalConfig.requireDocuments.length > 0 && (
                            <div className="mt-2">
                              <p className="mb-1 text-xs text-gray-500">General requirements:</p>
                              <div className="space-y-1">
                                {generalConfig.requireDocuments.map((doc, index) => (
                                  <div
                                    key={index}
                                    className="p-1 text-xs text-primary rounded bg-primary/5"
                                  >
                                    {doc}{" "}
                                    {ptConfig.requireDocuments &&
                                    ptConfig.requireDocuments.length > 0
                                      ? "(Replaced)"
                                      : "(Inherited)"}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* General Fields (Read-only) */}
                    {generalConfig.formFields.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>General Fields</CardTitle>
                          <CardDescription>
                            Fields inherited from general configuration (read-only)
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {generalConfig.formFields.map((field, index) => (
                              <div
                                key={field.id}
                                className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-gray-400">
                                      #{index + 1}
                                    </span>
                                    <span className="font-medium">
                                      {field.label || "Unnamed Field"}
                                    </span>
                                    <span className="text-xs text-gray-500">({field.type})</span>
                                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                      General
                                    </span>
                                    {field.required && (
                                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                        Required
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Custom Fields for this Patron Type */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle>Custom Fields for {patronType.name}</CardTitle>
                            <CardDescription>
                              Fields specific to this patron type ({customFields.length} field
                              {customFields.length !== 1 ? "s" : ""})
                            </CardDescription>
                          </div>
                          <Button onClick={() => handleAddCustomField(patronType.id)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Custom Field
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {customFields.length === 0 ? (
                            <p className="py-8 text-sm text-center text-gray-500">
                              No custom fields configured. Click "Add Custom Field" to add fields
                              specific to this patron type.
                            </p>
                          ) : (
                            customFields.map((field, index) => (
                              <div
                                key={field.id}
                                className="flex items-center justify-between p-3 transition-colors border rounded-lg hover:bg-gray-50"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-gray-400">
                                      #{generalConfig.formFields.length + index + 1}
                                    </span>
                                    <span className="font-medium">
                                      {field.label || "Unnamed Field"}
                                    </span>
                                    <span className="text-xs text-gray-500">({field.type})</span>
                                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                      Custom
                                    </span>
                                    {field.required && (
                                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                        Required
                                      </span>
                                    )}
                                  </div>
                                  {field.placeholder && (
                                    <p className="mt-1 ml-6 text-xs text-gray-500">
                                      Placeholder: {field.placeholder}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleMoveCustomField(patronType.id, field.id, "up")
                                    }
                                    disabled={field.order === 0}
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleMoveCustomField(patronType.id, field.id, "down")
                                    }
                                    disabled={field.order === customFields.length - 1}
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditCustomField(patronType.id, field)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCustomField(patronType.id, field.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Preview Panel */}
                  <div className="lg:col-span-1">
                    <Card>
                      <CardHeader>
                        <CardTitle>Live Preview</CardTitle>
                        <CardDescription>
                          How the form will look for {patronType.name}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {allFields.length === 0 ? (
                            <p className="py-8 text-sm text-center text-gray-500">
                              No fields to preview
                            </p>
                          ) : (
                            allFields.map((field) => (
                              <div key={field.id}>
                                <Label>
                                  {field.label}
                                  {field.required && <span className="text-red-500">*</span>}
                                  {generalConfig.formFields.some((f) => f.id === field.id) ? (
                                    <span className="ml-2 text-xs text-primary">(General)</span>
                                  ) : (
                                    <span className="ml-2 text-xs text-purple-600">(Custom)</span>
                                  )}
                                </Label>
                                {field.type === "text" && (
                                  <Input placeholder={field.placeholder} disabled />
                                )}
                                {field.type === "email" && (
                                  <Input type="email" placeholder={field.placeholder} disabled />
                                )}
                                {field.type === "number" && (
                                  <Input type="number" placeholder={field.placeholder} disabled />
                                )}
                                {field.type === "textarea" && (
                                  <textarea
                                    className="w-full min-h-[80px] px-3 py-2 border rounded-md"
                                    placeholder={field.placeholder}
                                    disabled
                                  />
                                )}
                                {field.type === "select" && field.options && (
                                  <Select disabled>
                                    <SelectTrigger>
                                      <SelectValue placeholder={field.placeholder || "Select..."} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {field.options.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between">
                  <Button
                    onClick={() => handleResetPatronType(patronType.id)}
                    disabled={saving}
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {pendingKey === `reset:${patronType.id}` ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="mr-2 h-4 w-4" />
                    )}
                    Reset to Defaults
                  </Button>
                  <Button
                    onClick={() => handleSavePatronType(patronType.id)}
                    disabled={saving}
                    size="lg"
                  >
                    {pendingKey === `save:${patronType.id}` ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {`Save ${patronType.name} Configuration`}
                  </Button>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </PageContainer>

      {/* Field Configuration Modal */}
      {showFieldModal && editingField && (
        <FieldConfigModal
          field={editingField}
          onSave={() => {
            if (editingFieldType === "general") {
              handleSaveGeneralField();
            } else if (activeTab !== "general") {
              handleSaveCustomField(activeTab);
            }
          }}
          onCancel={() => {
            setShowFieldModal(false);
            setEditingField(null);
          }}
          onChange={(updated) => setEditingField(updated)}
        />
      )}
    </AppLayout>
  );
};

// Field Configuration Modal Component
const FieldConfigModal = ({
  field,
  onSave,
  onCancel,
  onChange,
}: {
  field: FormFieldConfig;
  onSave: () => void;
  onCancel: () => void;
  onChange: (field: FormFieldConfig) => void;
}) => {
  const generateFieldName = (label: string) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  const handleLabelChange = (label: string) => {
    onChange({
      ...field,
      label,
      name: field.name || generateFieldName(label),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{field.id.startsWith("field-") ? "Add Field" : "Edit Field"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Field Type</Label>
            <Select
              value={field.type}
              onValueChange={(value: FormFieldConfig["type"]) =>
                onChange({ ...field, type: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Label</Label>
            <Input
              value={field.label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder="e.g., Matric Number"
            />
          </div>

          <div>
            <Label>Field Name</Label>
            <Input
              value={field.name}
              onChange={(e) => onChange({ ...field, name: e.target.value })}
              placeholder="e.g., matric_number"
            />
            <p className="mt-1 text-xs text-gray-500">
              Used internally (no spaces, use underscores)
            </p>
          </div>

          <div>
            <Label>Placeholder</Label>
            <Input
              value={field.placeholder || ""}
              onChange={(e) => onChange({ ...field, placeholder: e.target.value })}
              placeholder="e.g., Enter your matric number"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="required"
              checked={field.required}
              onCheckedChange={(checked) => onChange({ ...field, required: checked === true })}
            />
            <Label htmlFor="required">Required Field</Label>
          </div>

          {(field.type === "select" || field.type === "radio") && (
            <div>
              <Label>Options</Label>
              <div className="mt-2 space-y-2">
                {field.options?.map((opt, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={opt.label}
                      onChange={(e) => {
                        const newOptions = [...(field.options || [])];
                        newOptions[index].label = e.target.value;
                        onChange({ ...field, options: newOptions });
                      }}
                      placeholder="Label"
                    />
                    <Input
                      value={opt.value}
                      onChange={(e) => {
                        const newOptions = [...(field.options || [])];
                        newOptions[index].value = e.target.value;
                        onChange({ ...field, options: newOptions });
                      }}
                      placeholder="Value"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newOptions = field.options?.filter((_, i) => i !== index) || [];
                        onChange({ ...field, options: newOptions });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newOptions = [...(field.options || []), { label: "", value: "" }];
                    onChange({ ...field, options: newOptions });
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Option
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onSave}>Save Field</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationSetupPage;
